/**
 * Privileged reveal of complete customer data (owner decision OD-41): COMPLETE ACCESS + CONTROLLED
 * EXPOSURE + IMMUTABLE AUDIT. Lists stay masked; in Customer 360 the Full Admin deliberately reveals
 * the full value. The server requires a recent admin step-up (this module asks for the admin TOTP when
 * it is missing) and records one audit event per reveal — without the value. Revealed values live only
 * in component state (never storage, never logs) and are hidden again on «پنهان کردن» or navigation.
 */
import { Button, Dialog, formatJalali, TextField } from '@hopex/ui';
import { Copy, Eye, EyeOff } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { api, ApiFailure, errorMessage } from '../api.ts';

type Field = 'IDENTITY' | 'PHONE' | 'NATIONAL_ID' | 'BANK_INSTRUMENT';
export interface RevealedIdentity {
  publicId: string;
  phone: string | null;
  nationalId: string | null;
  firstName: string | null;
  lastName: string | null;
  birthDate: string | null;
  fatherName: string | null;
}
type Result =
  | { field: 'IDENTITY'; identity: RevealedIdentity }
  | { field: 'PHONE' | 'NATIONAL_ID'; value: string | null }
  | { field: 'BANK_INSTRUMENT'; instrumentId: string; kind: 'CARD' | 'IBAN'; value: string };

/** «+989121234567» → «09121234567» (the form operators and customers use). */
const localPhone = (e164: string | null) =>
  e164 ? (e164.startsWith('+98') ? `0${e164.slice(3)}` : e164) : null;

/** Calls the reveal endpoint; on STEP_UP_REQUIRED asks for the admin TOTP, steps up, retries once. */
function useReveal(userId: string) {
  const [pending, setPending] = useState<{
    body: { field: Field; instrumentId?: string };
    resolve: (r: Result | null) => void;
  } | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const url = `/users/${userId}/reveal`;

  async function reveal(body: { field: Field; instrumentId?: string }): Promise<Result | null> {
    setError(null);
    try {
      return await api.post<Result>(url, body);
    } catch (e) {
      if (e instanceof ApiFailure && e.code === 'STEP_UP_REQUIRED')
        return new Promise((resolve) => setPending({ body, resolve }));
      setError(errorMessage(e));
      return null;
    }
  }
  async function confirm() {
    if (!pending) return;
    setBusy(true);
    setError(null);
    try {
      await api.post('/auth/step-up', { code });
      const r = await api.post<Result>(url, pending.body);
      setCode('');
      pending.resolve(r);
      setPending(null);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }
  function cancel() {
    pending?.resolve(null);
    setPending(null);
    setCode('');
  }
  const dialog = pending ? (
    <Dialog
      isOpen
      onOpenChange={(o) => !o && cancel()}
      title="تأیید هویت مدیر"
      isDismissDisabled={busy}
      footer={
        <>
          <Button variant="ghost" onPress={cancel} isDisabled={busy}>
            انصراف
          </Button>
          <Button loading={busy} onPress={() => void confirm()}>
            نمایش
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3" data-reveal-step-up>
        <p className="m-0 text-[13px] leading-7 text-fg-muted">
          نمایش اطلاعات کامل در گزارش رویدادها ثبت می‌شود. برای ادامه، کد فعلی برنامه‌ی
          Authenticator مدیر را وارد کنید.
        </p>
        <TextField
          label="کد ورود دومرحله‌ای مدیر"
          value={code}
          onChange={setCode}
          ltr
          inputMode="numeric"
        />
        {error && (
          <p role="alert" className="m-0 text-[13px] text-negative">
            {error}
          </p>
        )}
      </div>
    </Dialog>
  ) : null;
  return { reveal, dialog, error: pending ? null : error };
}

function CopyValue({ value, label }: { value: string; label: string }) {
  const [done, setDone] = useState(false);
  return (
    <span className="inline-flex items-center gap-1.5">
      <bdi dir="ltr" className="font-mono text-fg">
        {value}
      </bdi>
      <button
        type="button"
        aria-label={`کپی ${label}`}
        onClick={() =>
          void navigator.clipboard?.writeText(value).then(() => {
            setDone(true);
            setTimeout(() => setDone(false), 1500);
          })
        }
        className="grid size-7 cursor-pointer place-items-center rounded-sm text-fg-muted hover:bg-surface-2 hover:text-fg focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-focus"
      >
        <Copy aria-hidden className="size-3.5" />
      </button>
      {done && <span className="text-[11px] text-positive">کپی شد</span>}
    </span>
  );
}

/** Customer 360 «نمایش اطلاعات کامل»: complete identity after an audited, step-up-gated reveal. */
export function IdentityReveal({ userId }: { userId: string }) {
  const { reveal, dialog, error } = useReveal(userId);
  const [identity, setIdentity] = useState<RevealedIdentity | null>(null);
  const [busy, setBusy] = useState(false);
  const row = (label: string, value: string | null, mono = false): ReactNode => (
    <div key={label} className="flex flex-col gap-0.5">
      <dt className="text-[12px] text-fg-muted">{label}</dt>
      <dd className="m-0 text-[13.5px] font-semibold">
        {value ? mono ? <CopyValue value={value} label={label} /> : value : '—'}
      </dd>
    </div>
  );
  return (
    <div
      className="mt-3 border-t border-line pt-3"
      data-identity-reveal={identity ? 'open' : 'closed'}
    >
      {identity ? (
        <>
          <div className="mb-2 flex items-center justify-between gap-2">
            <p className="m-0 text-[12px] font-semibold text-accent-text">
              اطلاعات کامل (نمایش ثبت شد)
            </p>
            <Button variant="ghost" size="sm" onPress={() => setIdentity(null)}>
              <EyeOff aria-hidden className="size-4" />
              پنهان کردن
            </Button>
          </div>
          <dl className="m-0 grid gap-3 sm:grid-cols-3" data-revealed-identity>
            {row('نام', identity.firstName)}
            {row('نام خانوادگی', identity.lastName)}
            {row('نام پدر', identity.fatherName)}
            {row('موبایل کامل', localPhone(identity.phone), true)}
            {row('کد ملی کامل', identity.nationalId, true)}
            {row(
              'تاریخ تولد',
              identity.birthDate ? formatJalali(identity.birthDate, { time: false }) : null,
            )}
          </dl>
        </>
      ) : (
        <Button
          variant="secondary"
          size="sm"
          loading={busy}
          onPress={() => {
            setBusy(true);
            void reveal({ field: 'IDENTITY' }).then((r) => {
              setBusy(false);
              if (r?.field === 'IDENTITY') setIdentity(r.identity);
            });
          }}
          data-reveal-identity
        >
          <Eye aria-hidden className="size-4" />
          نمایش اطلاعات کامل
        </Button>
      )}
      {error && (
        <p role="alert" className="m-0 mt-2 text-[13px] text-negative">
          {error}
        </p>
      )}
      {dialog}
    </div>
  );
}

/** Banking tab: full card number / IBAN for one instrument after an audited reveal. */
export function InstrumentReveal({
  userId,
  instrumentId,
  masked,
}: {
  userId: string;
  instrumentId: string;
  masked: string;
}) {
  const { reveal, dialog, error } = useReveal(userId);
  const [value, setValue] = useState<string | null>(null);
  return (
    <span className="flex flex-col gap-1" data-instrument-reveal={value ? 'open' : 'closed'}>
      {value ? (
        <span className="inline-flex items-center gap-1">
          <CopyValue value={value} label="شماره‌ی کامل" />
          <button
            type="button"
            aria-label="پنهان کردن شماره‌ی کامل"
            onClick={() => setValue(null)}
            className="grid size-7 cursor-pointer place-items-center rounded-sm text-fg-muted hover:bg-surface-2 hover:text-fg focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-focus"
          >
            <EyeOff aria-hidden className="size-3.5" />
          </button>
        </span>
      ) : (
        <span className="inline-flex items-center gap-1">
          <bdi dir="ltr" className="font-mono">
            {masked}
          </bdi>
          <button
            type="button"
            aria-label="نمایش شماره‌ی کامل"
            onClick={() =>
              void reveal({ field: 'BANK_INSTRUMENT', instrumentId }).then((r) => {
                if (r?.field === 'BANK_INSTRUMENT') setValue(r.value);
              })
            }
            className="grid size-7 cursor-pointer place-items-center rounded-sm text-fg-muted hover:bg-surface-2 hover:text-fg focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-focus"
            data-reveal-instrument
          >
            <Eye aria-hidden className="size-3.5" />
          </button>
        </span>
      )}
      {error && (
        <span role="alert" className="text-[12px] text-negative">
          {error}
        </span>
      )}
      {dialog}
    </span>
  );
}
